import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ClipboardList, Store, Users, Package, Settings as SettingsIcon, LogOut, Menu, X, Shield, CreditCard } from 'lucide-react';
import { useAuthStore } from '../store/auth';

const navItems = [
  { path: '/applications', label: 'Arizalar', icon: ClipboardList },
  { path: '/shops', label: "Do'konlar", icon: Store },
  { path: '/subscriptions', label: 'Obunalar', icon: CreditCard },
  { path: '/sellers', label: 'Sotuvchilar', icon: Users },
  { path: '/products', label: 'Tovarlar', icon: Package },
  { path: '/settings', label: 'Sozlamalar', icon: SettingsIcon },
];

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [confirmLogout, setConfirmLogout] = React.useState(false);

  const handleLogout = () => {
    setConfirmLogout(false);
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex bg-gray-100">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-slate-900 text-slate-100 transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center gap-2 px-6 h-16 border-b border-slate-700">
          <Shield className="w-6 h-6 text-primary-400" />
          <span className="font-bold text-lg">Super Admin</span>
        </div>
        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path || (item.path === '/applications' && location.pathname === '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                  active ? 'bg-primary-600/15 text-primary-300' : 'text-slate-300 hover:bg-slate-800'
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white shadow-sm flex items-center justify-between px-4 lg:px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen((v) => !v)}>
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600 hidden sm:block">{user?.username}</span>
            <button onClick={() => setConfirmLogout(true)} className="flex items-center gap-2 text-sm text-gray-600 hover:text-red-600">
              <LogOut className="w-4 h-4" /> Chiqish
            </button>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-auto">{children}</main>
      </div>

      {confirmLogout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Chiqish</h2>
            <p className="text-sm text-gray-600 mb-4">Siz rostdan ham chiqishni hohlaysizmi?</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmLogout(false)} className="btn btn-secondary">Yo'q</button>
              <button onClick={handleLogout} className="btn btn-danger" autoFocus>Ha, chiqaman</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
